import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useWindowSize } from 'hooks/useWindowSize'
import { useSprintPacket, useUpdateSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentAnchor, CommentThread } from 'components/comments/CommentThread'
import { AllCommentsDrawer } from 'components/comments/AllCommentsDrawer'
import { anchorSectionKey } from 'components/comments/groupCommentsByAnchor'
import { countUnresolvedTotal } from 'components/comments/commentCounts'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { EditorTOC } from './EditorTOC'
import { PacketHeader } from './PacketHeader'
import { useActiveSection } from './useActiveSection'
import { useCommentAnchors } from './useCommentAnchors'
import { UnresolvedCommentsProvider } from './UnresolvedCommentsContext'
import { PacketSectionsProvider } from './PacketSectionsContext'
import { editorNavSections, mergeSections, SECTIONS, SectionKey } from './sections'
import { dark, light, radius, type } from '../tokens'
import { PerformanceReviewSection } from './sections/PerformanceReviewSection'
import { NarrativeSection } from './sections/NarrativeSection'
import { ClientSurveySection } from './sections/ClientSurveySection'
import { AudienceSection } from './sections/AudienceSection'
import { BrandStandardsSection } from './sections/BrandStandardsSection'
import { CreativeSection } from './sections/CreativeSection'
import { CustomSection } from './sections/CustomSection'
import { AddCustomSectionButton } from './sections/AddCustomSectionButton'
import { packetReviewCommentUrl } from '../../creatives/creativeUrl'
import { audiencesUrl } from '../../audiences/audienceUrl'

const HEADER_OFFSET = 60

interface Props {
  businessID: number
  packetID: number
}

export function PacketEditorBody({ businessID, packetID }: Props) {
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const business = useGetBusinessIfExists()
  const [windowHeight] = useWindowSize()
  const ready = !isLoading && !isError && !!packet
  const { anchor, open, close, setAnchor, setOpen, rootRef, setRoot } = useCommentAnchors()
  // Memoized: `useActiveSection`'s effect keys off this identity, and a fresh
  // array every render would rebuild the observer on each packet refetch.
  const customSections = useMemo(() => packet?.customSections ?? [], [packet?.customSections])
  const allSections = useMemo(() => mergeSections(SECTIONS, customSections), [customSections])
  const hiddenSections = useMemo(() => packet?.hiddenSections ?? [], [packet?.hiddenSections])
  // Edit's own deck and numbering source (`editorNavSections`) — every
  // AE-visible row, including a linkless client survey, which is why it can
  // diverge from what Present numbers (`visiblePacketSections`) for a packet
  // that isn't configured yet. Rendering still iterates `allSections` below,
  // so an AE-hidden card stays on screen (recessed) and there's a way back.
  const navSections = useMemo(
    () => editorNavSections(allSections, hiddenSections),
    [allSections, hiddenSections]
  )
  // One refetch per click rather than a hand-patched cache: the same round trip
  // every narrative autosave in this editor already pays, and an eye click is
  // far rarer than an autosave.
  const { mutate: mutatePacket, isLoading: hidePending } = useUpdateSprintPacket(packetID)
  const hide = useMemo(
    () => ({
      hidden: new Set(hiddenSections),
      toggle: (key: SectionKey, hidden: boolean) =>
        mutatePacket({ hiddenSection: { key, hidden } }),
      pending: hidePending,
    }),
    [hiddenSections, mutatePacket, hidePending]
  )
  const activeKey = useActiveSection({ sections: navSections, scrollRootRef: rootRef, ready })
  const handledDeepLinkRef = useRef(false)
  const [allCommentsOpen, setAllCommentsOpen] = useState(false)
  const [threadTargetCommentId, setThreadTargetCommentId] = useState<number | null>(null)

  // Navigate the editor to an anchor and open its thread. Scrolls to the entity
  // element when one is mounted (creative/audience cards), else to the owning
  // section via `anchorSectionKey`. Shared by the Slack deep-link effect and the
  // "All comments" list selection. `commentId` drives CommentThread's in-app
  // highlight; omit it for deep-links (CommentThread reads ?commentId= itself).
  const openAnchor = useCallback(
    (target: CommentAnchor, commentId?: number | null) => {
      const sectionKey = anchorSectionKey(target.type, target.id)
      const entityEl = document.querySelector<HTMLElement>(
        `[data-comment-anchor-type="${target.type}"][data-comment-anchor-id="${target.id}"]`
      )
      const el = entityEl ?? (sectionKey ? document.getElementById(sectionKey) : null)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setThreadTargetCommentId(commentId ?? null)
      setAnchor(target)
      setOpen(true)
    },
    [setAnchor, setOpen]
  )

  // Open the right thread when arriving via a Slack comment deep-link
  // (`?commentId=&anchorType=&anchorId=`). Packets have many anchors, so unlike
  // CreativeWizard we must set the anchor before opening the drawer; once open,
  // CommentThread's own `?commentId=` effect scrolls + highlights the comment.
  // The ref latch keeps this run-once so a later refetch can't reopen the drawer.
  useEffect(() => {
    if (!ready || handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link) return
    handledDeepLinkRef.current = true
    openAnchor({ type: link.anchorType, id: link.anchorId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  const closeThread = useCallback(() => {
    close()
    setThreadTargetCommentId(null)
  }, [close])

  const onSelectComment = useCallback(
    (target: CommentAnchor, commentId: number) => {
      setAllCommentsOpen(false)
      if (target.type === 'creative') {
        window.open(
          packetReviewCommentUrl(businessID, packetID, target.id, commentId),
          '_blank',
          'noopener'
        )
        return
      }
      openAnchor(target, commentId)
    },
    [openAnchor, businessID, packetID]
  )

  const unresolvedTotal = useMemo(
    () => countUnresolvedTotal(packet?.comments ?? []),
    [packet?.comments]
  )
  // Dev-only: `?theme=dark` flips to the dark palette so v0.13 (PDF) and v0.14
  // (Present mode) can be visually validated through the real editor without
  // standing up Storybook. Defaults to `light` for AE-facing edit mode.
  const { search } = useLocation()
  const palette = new URLSearchParams(search).get('theme') === 'dark' ? dark : light

  const scrollHeight = windowHeight - HEADER_OFFSET

  return (
    <PacketSectionsProvider sections={navSections} hide={hide}>
      <UnresolvedCommentsProvider comments={packet?.comments ?? []}>
        <div
          ref={setRoot}
          style={{
            height: scrollHeight,
            maxHeight: scrollHeight,
            background: palette.bg,
            color: palette.text,
            fontFamily: type.family.sans,
          }}
          className="w-full overflow-y-auto"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <CircularProgress size={28} />
            </div>
          ) : isError || !packet ? (
            <div style={{ padding: 24 }}>
              <div
                style={{
                  background: palette.surface,
                  border: `1px solid ${palette.hairline}`,
                  borderRadius: radius.lg,
                  padding: 40,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  textAlign: 'center',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontFamily: type.family.sans,
                    fontSize: type.size.h2,
                    fontWeight: type.weight.semibold,
                    color: palette.text,
                  }}
                >
                  Couldn't load this sprint packet
                </h2>
                <p style={{ fontSize: type.size.body, color: palette.textMuted, maxWidth: 320 }}>
                  Something went wrong fetching the packet. Try again in a moment.
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  style={{
                    padding: '8px 16px',
                    borderRadius: radius.sm,
                    border: `1px solid ${palette.hairline}`,
                    background: palette.surface,
                    color: palette.text,
                    fontFamily: type.family.mono,
                    fontSize: type.size.eyebrow,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex w-full min-h-full">
              <EditorTOC
                palette={palette}
                sections={navSections}
                clientSurveyHidden={!packet?.survey.link}
                activeKey={activeKey}
                onOpenAllComments={() => setAllCommentsOpen(true)}
                unresolvedTotal={unresolvedTotal}
              />
              <div className="flex-1 flex flex-col min-w-0">
                <PacketHeader
                  palette={palette}
                  businessName={business?.businessName}
                  phase={business?.phase}
                  sprint={packet.sprint}
                />
                <main className="px-6 py-4 space-y-6 w-full">
                  {allSections.map((s, i) => {
                    switch (s.kind) {
                      case 'custom':
                        return (
                          <CustomSection
                            key={s.key}
                            palette={palette}
                            packetID={packet.id}
                            section={s.section}
                            canMoveUp={i > 0}
                            canMoveDown={i < allSections.length - 1}
                          />
                        )
                      case 'performance':
                        return (
                          <PerformanceReviewSection key={s.key} palette={palette} packet={packet} />
                        )
                      case 'narrative':
                        return (
                          <NarrativeSection
                            key={s.key}
                            palette={palette}
                            sectionKey={s.key}
                            label={s.label}
                            packetID={packet.id}
                            narrative={packet.narrative}
                          />
                        )
                      case 'client-survey':
                        return (
                          <ClientSurveySection
                            key={s.key}
                            palette={palette}
                            businessID={packet.businessID}
                            packetID={packet.id}
                            survey={packet.survey}
                            businessConnected={!!business?.sfAccountId}
                          />
                        )
                      case 'audience':
                        return (
                          <AudienceSection
                            key={s.key}
                            palette={palette}
                            businessID={packet.businessID}
                            packetID={packet.id}
                            audiences={packet.audiences}
                            audienceSelection={packet.audienceSelection ?? null}
                          />
                        )
                      case 'brand':
                        return (
                          <BrandStandardsSection
                            key={s.key}
                            palette={palette}
                            packetID={packet.id}
                            brandStandards={packet.brandStandards ?? null}
                            brandLogos={packet.brandLogos}
                          />
                        )
                      case 'creative':
                        return (
                          <CreativeSection
                            key={s.key}
                            palette={palette}
                            packetID={packet.id}
                            sprintID={packet.sprintID}
                            narrative={packet.narrative ?? []}
                          />
                        )
                    }
                  })}
                  <AddCustomSectionButton palette={palette} packetID={packet.id} />
                  <div className="h-[60vh]" aria-hidden />
                </main>
              </div>
            </div>
          )}
        </div>
        {packet && (
          <AllCommentsDrawer
            open={allCommentsOpen}
            onClose={() => setAllCommentsOpen(false)}
            palette={palette}
            comments={packet.comments}
            // The *unfiltered* deck: labels only. A comment on a hidden slide
            // reads "Brand Standards" rather than a raw `7:brand-standards`
            // anchor, and stays in the location filter. Its number falls back to
            // `00` — the drawer takes numbers from `numberFor`, which is built
            // off the visible deck.
            sections={allSections}
            audiences={packet.audiences}
            onSelectComment={onSelectComment}
          />
        )}
        {packet && (
          <CommentThread
            open={open}
            onClose={closeThread}
            businessID={businessID}
            anchor={anchor}
            targetCommentId={threadTargetCommentId}
            // A comment mutation only invalidates the per-anchor comments query, not
            // the packet — refetch the packet so the hydrated comments (the badge
            // source) refresh and an anchor's unresolved badge clears on resolve.
            onCommentsChanged={refetch}
            notificationContext={{
              header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
              pageUrlBase: window.location.href.split('?')[0],
              pageUrlBaseForAnchor: a =>
                a.type === 'audience' ? audiencesUrl(businessID) : undefined,
              pageUrlForAnchor: (a, commentId) =>
                a.type === 'creative'
                  ? packetReviewCommentUrl(businessID, packetID, a.id, commentId)
                  : undefined,
              slackChannelId: business?.slackChannelId,
            }}
          />
        )}
      </UnresolvedCommentsProvider>
    </PacketSectionsProvider>
  )
}
